import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaTwitter, FaYoutube } from 'react-icons/fa';
import wdLogo from '../../assets/wd-logo.svg';
import { SERVICES, getFooterHref } from './landingData';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const styles = `
  @keyframes spin { to { transform: rotate(360deg); } }
  .footer-loading-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(0, 0, 0, 0.2);
    border-top-color: #000;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    display: inline-block;
  }
`;

const sectionLinks = {
  'About Us': '/about',
  'Contact': '#contact',
  'Our Work': '/work',
  'Blog': '/blog',
  'Reviews': '#testimonials',
  'Terms of Service': '/terms',
  'Privacy Policy': '/privacy',
};

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const LandingFooter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [subEmail, setSubEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subEmail.trim()) return toast.error('Please enter your email.');
    setSubscribing(true);
    try {
      const res = await api.post('/api/newsletter', { email: subEmail });
      if (res.data?.success) {
        toast.success(res.data.message || 'Subscribed successfully!');
        setSubEmail('');
      } else {
        toast.error(res.data?.message || 'Failed to subscribe.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to subscribe.');
    } finally {
      setSubscribing(false);
    }
  };

  const handleLink = (label) => {
    if (label === 'About Us') {
      navigate('/about');
      return;
    }

    if (label === 'Terms of Service' || label === 'Privacy Policy') {
      navigate(sectionLinks[label]);
      return;
    }

    const target = sectionLinks[label] || getFooterHref(label);

    if (target.startsWith('#')) {
      if (location.pathname === '/') {
        scrollToSection(target.slice(1));
        window.history.replaceState(null, '', target);
        return;
      }
      navigate({ pathname: '/', hash: target });
      return;
    }

    navigate(target);
  };

  return (
  <>
    <style>{styles}</style>
    <footer className="landing-footer">
    <div className="landing-footer-inner">
      <div>
        <a href="/" aria-label="WebDigiting home" style={{ display:'block', marginBottom:12, lineHeight:0, textDecoration:'none' }}>
          <img src={wdLogo} alt="" width={72} height={44} style={{ height:44, width:'auto', display:'block', objectFit:'contain' }} />
        </a>
        <p style={{ fontSize:13, color:'var(--muted-light)', maxWidth:230, lineHeight:1.7, marginBottom:16 }}>Your end-to-end digital growth partner. From idea to impact.</p>
        
        {/* Newsletter Subscription */}
        <div style={{ marginTop: 8 }}>
          <p style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 10 }}>Subscribe to Newsletter</p>
          <form onSubmit={handleSubscribe} style={{ display: 'flex', gap: 8 }}>
            <input
              type="email"
              placeholder="Enter your email"
              value={subEmail}
              onChange={(e) => setSubEmail(e.target.value)}
              style={{
                flex: 1,
                padding: '10px 12px',
                fontSize: 13,
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 4,
                color: 'var(--white)',
                outline: 'none'
              }}
              required
            />
            <button
              type="submit"
              disabled={subscribing}
              style={{
                padding: '10px 16px',
                fontSize: 12,
                fontWeight: 500,
                background: 'var(--accent)',
                border: 'none',
                borderRadius: 4,
                color: '#000',
                cursor: subscribing ? 'not-allowed' : 'pointer',
                opacity: subscribing ? 0.7 : 1
              }}
            >
              {subscribing ? (
                <span className="footer-loading-spinner" aria-hidden="true" />
              ) : (
                'Subscribe'
              )}
            </button>
          </form>
        </div>
      </div>
      <div style={{ display:'flex', gap:60, flexWrap:'wrap' }}>
        {[{ title:'Services', links: SERVICES.slice(0,5).map((s)=>s.name) },
          { title:'More Services', links: SERVICES.slice(5).map((s)=>s.name) },
          { title:'Company', links:['About Us','Our Work','Blog','Reviews','Contact'] },
          { title:'Legal', links:['Terms of Service','Privacy Policy'] }
        ].map(col => (
          <div key={col.title}>
            <p style={{ fontSize:10, letterSpacing:3, textTransform:'uppercase', color:'var(--accent)', marginBottom:16 }}>{col.title}</p>
            <ul style={{ listStyle:'none', display:'flex', flexDirection:'column', gap:10, margin:0, padding:0 }}>
              {col.links.map((l) => (
                <li key={l}>
                  <a
                    href={getFooterHref(l)}
                    style={{ color:'var(--muted-light)', fontSize:13, textDecoration:'none', cursor:'pointer' }}
                    onClick={(e) => { e.preventDefault(); handleLink(l); }}
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
    <div className="landing-footer-bottom">
      <p style={{ fontSize:11, color:'var(--muted)', letterSpacing:1 }}>© {new Date().getFullYear()} WebDigiting. All rights reserved.</p>
      <div style={{ display:'flex', gap:8 }}>
        {[
          { name:'facebook', href:'https://www.facebook.com/webdigitingofficial', Icon: FaFacebookF, label:'Facebook' },
          { name:'instagram', href:'https://www.instagram.com/webdigitingofficial', Icon: FaInstagram, label:'Instagram' },
          { name:'linkedin', href:'https://www.linkedin.com/company/webdigiting', Icon: FaLinkedinIn, label:'LinkedIn' },
          { name:'twitter', href:'https://www.twitter.com/webdigiting', Icon: FaTwitter, label:'Twitter' },
          { name:'youtube', href:'https://www.youtube.com/@webdigitingyt', Icon: FaYoutube, label:'YouTube' }
        ].map((item) => (
          <a key={item.name} href={item.href} target="_blank" rel="noopener noreferrer" style={{ width:34, height:34, border:'1px solid rgba(255,255,255,.08)', borderRadius:2, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--muted-light)', textDecoration:'none', transition:'all .3s' }}
            onMouseEnter={(e)=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.color='var(--accent)';}}
            onMouseLeave={(e)=>{e.currentTarget.style.borderColor='rgba(255,255,255,.08)';e.currentTarget.style.color='var(--muted-light)';}}>
            <item.Icon size={14} />
          </a>
        ))}
      </div>
    </div>
  </footer>
  </>
  );
};

export default LandingFooter;
